import ResizeObserver from 'resize-observer-polyfill';

import { Component, Input, Output, EventEmitter, ElementRef, TemplateRef, OnInit } from '@angular/core';
import { coerceBooleanProperty, coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';

import { KillOnDestroy } from '@sg/utils';
import { Color, ColorHelper, ScaleType } from '@swimlane/ngx-charts';
import { Doughnut } from './doughnut.model';

export interface SgSecuritiesPieTemplateContext<T = any> {
  $implicit: T;
}

@Component({
  selector: 'sg-securities-pie',
  templateUrl: './securities-pie.component.html',
  styleUrls: ['./securities-pie.component.scss'],
  standalone: false,
})
@KillOnDestroy()
export class SgSecuritiesPieComponent<T = Doughnut> implements OnInit {
  @Input() set dataSet(value: { name: string; value: number }[]) {
    this.pieData = {
      view: [this.pieSize, this.pieSize],
      scheme: {
        name: 'bar-chart-scheme',
        selectable: false,
        domain: this._scheme.slice(),
        group: ScaleType.Linear,
      },
      results: value,
    };
  }

  @Input() arcOuterStrokeColor: string = '#405a72';

  @Input() set scheme(value: string[]) {
    this._scheme = value;
    if (this.pieData) {
      this.pieData = {
        ...this.pieData,
        scheme: {
          name: 'bar-chart-scheme',
          selectable: false,
          domain: value,
          group: ScaleType.Linear,
        },
      };
    }
  }

  @Input() get pieSize(): number {
    return this._pieSize;
  }
  set pieSize(value: NumberInput) {
    this._pieSize = coerceNumberProperty(value);
    if (this.pieData) {
      this.pieData.view = [this.pieSize, this.pieSize];
    }
  }

  @Input() title: string;
  @Input() doughnut: T;
  @Input() legend: boolean;
  @Input() legendPosition: string;
  @Input() legendTitle: string;

  @Input() get autoSize(): boolean {
    return this._autoSize;
  }
  set autoSize(value: boolean) {
    value = coerceBooleanProperty(value);
    if (value !== this._autoSize) {
      this._autoSize = value;
      if (value) {
        requestAnimationFrame(() => {
          this.pieSize = this.elRef.nativeElement.getBoundingClientRect().height;
        });
      }
    }
  }

  @Output() buttonClick = new EventEmitter<void>();

  sqrt2 = Math.sqrt(2);
  pieData: { view: [number, number]; scheme: Color; results: Array<{ name: string; value: any }> };
  activeEntries: any[] = [];
  legendChartNames: string[];
  legendColors: ColorHelper;

  doughnutViewTpl: TemplateRef<SgSecuritiesPieTemplateContext<T>>;

  private _scheme: string[] = ['#c49b4b', 'transparent'];
  private _pieSize: number = 300;
  private _autoSize = false;
  private ro: ResizeObserver;

  constructor(private elRef: ElementRef<HTMLElement>) {
    this.ro = new ResizeObserver((entries) => this.onResize(entries));
    this.ro.observe(elRef.nativeElement);
  }

  ngOnInit(): void {
    if (this.legend) {
      this.legendChartNames = this.pieData.results.map((d: any) => d.name);
      //@ts-ignore //Bug in ngx charts https://github.com/swimlane/ngx-charts/issues/1673
      this.legendColors = new ColorHelper(
        this.pieData.scheme,
        ScaleType.Ordinal,
        this.legendChartNames,
        this.pieData.scheme
      );
    }
  }

  legendLabelActivate(item: any): void {
    this.activeEntries = [item];
  }

  legendLabelDeactivate(): void {
    this.activeEntries = [];
  }

  registerTemplate(tpl: TemplateRef<SgSecuritiesPieTemplateContext<T>>) {
    this.doughnutViewTpl = tpl;
  }

  getDoughnutProperty(property: string): any {
    return (this.doughnut as any)?.[property];
  }

  private onResize(entries: ResizeObserverEntry[]): void {
    if (this.autoSize) {
      const entry = entries[0];
      this.pieSize = entry.contentRect.height;
    }
  }
}
